import { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import ErrorsComponent from "components/Errors";
import LoadIndicator from "components/LoadIndicator";
import { useMessageCases } from "hooks/useMessageCases";

const REQUEST_TEXT =
  "Please list the country, postal reference, region and town";
const MAX_LENGTH = 200;
const SENT_DISPLAY_MS = 900;

/**
 * Dialogue shown from "Not listed" on the Add Vendor Location form.
 * Sends the typed location request straight away, opening a message case.
 *
 * Stacked on top of the fullscreen AddVendorLocationModal; the z-index for
 * stacked modals is already handled in public/assets/css/style.css.
 */
function NewLocationMessage({ show, handleClose, onSent }) {
  const controller = useMessageCases();

  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState([]);

  const sentTimer = useRef(null);

  // Every open starts from a clean slate.
  useEffect(() => {
    if (show) {
      setMessage("");
      setSaving(false);
      setSent(false);
      setErrors([]);
    }
  }, [show]);

  useEffect(() => {
    return () => {
      if (sentTimer.current) clearTimeout(sentTimer.current);
    };
  }, []);

  const handleChange = useCallback((e) => {
    setMessage(e.target.value);
    setErrors([]);
  }, []);

  const handleBack = useCallback(() => {
    if (saving || sent) return;

    handleClose();
  }, [saving, sent, handleClose]);

  const handleOk = useCallback(async () => {
    if (saving || sent) return;

    const text = message.trim();

    if (!text) {
      setErrors(["Please enter the country, postal reference, region and town"]);
      return;
    }

    setErrors([]);
    setSaving(true);

    const { errors: sendErrors, newCase } =
      await controller.httpNewLocationMessage(text);

    setSaving(false);

    if (sendErrors && sendErrors.length > 0) {
      setErrors(sendErrors);
      return;
    }

    if (!newCase) {
      setErrors(["Something went wrong, check your internet"]);
      return;
    }

    setSent(true);

    sentTimer.current = setTimeout(() => {
      handleClose();
      if (onSent) onSent();
    }, SENT_DISPLAY_MS);
  }, [saving, sent, message, controller, handleClose, onSent]);

  return (
    <Modal
      show={show}
      onHide={handleBack}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header
        closeButton
        closeVariant="white"
        style={{ borderBottom: "none" }}
      >
        <Modal.Title>Location not listed</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: 8, paddingTop: 0 }}>
        <div className="px-4">
          <h5 className="mod-detail px-0 pt-0">{REQUEST_TEXT}</h5>

          <textarea
            className="form-control"
            rows={3}
            maxLength={MAX_LENGTH}
            value={message}
            onChange={handleChange}
            disabled={saving || sent}
            autoFocus
            aria-label="Location request details"
          />

          <div className="d-flex justify-content-end mt-1">
            <small className="text-muted">
              {message.length}/{MAX_LENGTH}
            </small>
          </div>

          {errors.length > 0 && <ErrorsComponent errors={errors} />}

          {sent && <p className="success_message">Request sent</p>}
        </div>
      </Modal.Body>

      <Modal.Footer style={{ borderTop: "none" }}>
        <button
          type="button"
          className="btn btn-black me-2"
          onClick={handleBack}
          disabled={saving || sent}
        >
          Back
        </button>

        <button
          type="button"
          className="btn btn-yellow"
          onClick={handleOk}
          disabled={saving || sent}
        >
          {saving ? <LoadIndicator /> : "OK"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default NewLocationMessage;
